import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AccountInput from '../components/AccountInput';

const emptyLine = () => ({ account_id: '', counter_account_id: '', debit: '', credit: '', description: '' });

// Udvid linjer med modkonti til fulde debet/kredit-par
function expandLines(lines) {
  const result = [];
  for (const line of lines) {
    if (!line.account_id || (!parseFloat(line.debit) && !parseFloat(line.credit))) continue;
    result.push({ account_id: line.account_id, debit: parseFloat(line.debit) || 0, credit: parseFloat(line.credit) || 0, description: line.description || '' });
    if (line.counter_account_id) {
      result.push({ account_id: line.counter_account_id, debit: parseFloat(line.credit) || 0, credit: parseFloat(line.debit) || 0, description: line.description || '' });
    }
  }
  return result;
}

export default function VoucherDetail() {
  const { id } = useParams();
  const { apiFetch, token, user } = useAuth();
  const [voucher, setVoucher] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [reversing, setReversing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editDate, setEditDate] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editLines, setEditLines] = useState([]);
  const [error, setError] = useState('');
  const [editError, setEditError] = useState('');
  const fileRef = useRef();

  const API_BASE = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';

  const load = () => {
    apiFetch(`/api/vouchers/${id}`).then(data => { setVoucher(data); setError(''); }).catch(err => setError(err.message));
  };

  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    apiFetch('/api/accounts').then(setAccounts).catch(() => {});
  }, [apiFetch]);

  const startEditing = () => {
    setEditDate(voucher.date?.split('T')[0] || '');
    setEditDescription(voucher.description || '');
    setEditLines(voucher.lines.map(l => ({
      account_id: String(l.account_id),
      counter_account_id: '',
      debit: l.debit > 0 ? String(l.debit) : '',
      credit: l.credit > 0 ? String(l.credit) : '',
      description: l.description || ''
    })));
    setEditError('');
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditError('');
  };

  const updateLine = (index, field, value) => {
    const updated = [...editLines];
    updated[index] = { ...updated[index], [field]: value };
    setEditLines(updated);
  };

  const addLine = () => setEditLines([...editLines, emptyLine()]);

  const removeLine = (index) => {
    if (editLines.length <= 2) return;
    setEditLines(editLines.filter((_, i) => i !== index));
  };

  const allEditLines = expandLines(editLines);
  const editTotalDebit = allEditLines.reduce((sum, l) => sum + l.debit, 0);
  const editTotalCredit = allEditLines.reduce((sum, l) => sum + l.credit, 0);
  const editDiff = editTotalDebit - editTotalCredit;
  const isBalanced = Math.abs(editDiff) < 0.01;
  const hasEditAmounts = editTotalDebit + editTotalCredit > 0;

  const addBalancingEntry = (accountId) => {
    const newLine = emptyLine();
    newLine.account_id = accountId;
    if (editDiff > 0) {
      newLine.credit = editDiff.toFixed(2);
    } else {
      newLine.debit = Math.abs(editDiff).toFixed(2);
    }
    setEditLines([...editLines, newLine]);
  };

  const handleSave = async () => {
    setEditError('');
    if (!editDescription.trim()) {
      setEditError('Beskrivelse er p\u00e5kr\u00e6vet.');
      return;
    }
    const expanded = expandLines(editLines);
    if (expanded.length < 2) {
      setEditError('Mindst to posteringslinjer er p\u00e5kr\u00e6vet.');
      return;
    }
    if (!isBalanced) {
      setEditError('Debet og kredit stemmer ikke overens.');
      return;
    }

    setSaving(true);
    try {
      await apiFetch(`/api/vouchers/${id}`, {
        method: 'PUT',
        body: { date: editDate, description: editDescription, lines: expanded }
      });
      setEditing(false);
      load();
    } catch (err) {
      setEditError(err.message);
    }
    setSaving(false);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await apiFetch(`/api/vouchers/${id}/attachments`, {
        method: 'POST',
        body: formData,
        isFormData: true
      });
      load();
    } catch (err) {
      alert(err.message);
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const deleteAttachment = async (attachmentId) => {
    if (!window.confirm('Slet denne vedh\u00e6ftning?')) return;
    try {
      await apiFetch(`/api/vouchers/${id}/attachments/${attachmentId}`, { method: 'DELETE' });
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  const fmt = (n) => parseFloat(n).toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if (error && !voucher) return <div className="alert alert-error">{error}</div>;
  if (!voucher) return <div className="loading">Indlæser...</div>;

  const canEdit = !voucher.is_reversed && !voucher.reverses_voucher_id && user?.role === 'admin';

  const totalDebit = voucher.lines?.reduce((sum, l) => sum + parseFloat(l.debit || 0), 0) || 0;
  const totalCredit = voucher.lines?.reduce((sum, l) => sum + parseFloat(l.credit || 0), 0) || 0;

  return (
    <div>
      <div className="page-header">
        <h1>Bilag #{voucher.voucher_number}</h1>
        <div style={{display: 'flex', gap: '0.5rem', alignItems: 'center'}}>
          {canEdit && !editing && (
            <button className="btn btn-primary" onClick={startEditing}>Rediger</button>
          )}
          {canEdit && !editing && (
            <button className="btn btn-danger" disabled={reversing} onClick={async () => {
              if (!window.confirm(`Tilbagef\u00f8r bilag #${voucher.voucher_number}? Der oprettes et modbogf\u00f8ringsbilag.`)) return;
              setReversing(true);
              try {
                const r = await apiFetch(`/api/vouchers/${id}/reverse`, { method: 'POST' });
                alert(r.message);
                load();
              } catch (err) { alert(err.message); }
              setReversing(false);
            }}>{reversing ? 'Tilbagef\u00f8rer...' : 'Tilbagef\u00f8r'}</button>
          )}
          <Link to="/app/bilag" className="btn btn-secondary">Tilbage til bilag</Link>
        </div>
      </div>

      {voucher.is_reversed && (
        <div className="alert alert-error" style={{marginBottom: '1rem'}}>
          Dette bilag er tilbageført.{' '}
          {voucher.reversed_by_voucher_id && (
            <Link to={`/app/bilag/${voucher.reversed_by_voucher_id}`}>Se modbogføring</Link>
          )}
        </div>
      )}
      {voucher.reverses_voucher_id && (
        <div className="alert alert-success" style={{marginBottom: '1rem'}}>
          Dette bilag er en tilbageførsel af{' '}
          <Link to={`/app/bilag/${voucher.reverses_voucher_id}`}>bilag #{voucher.description.match(/#(\d+)/)?.[1] || '?'}</Link>
        </div>
      )}

      {editing ? (
        <>
          {editError && <div className="alert alert-error" style={{marginBottom: '1rem'}}>{editError}</div>}

          <div className="card">
            <div className="form-row">
              <div className="form-group">
                <label>Dato</label>
                <input type="date" className="form-control" value={editDate}
                  onChange={e => setEditDate(e.target.value)} required />
              </div>
              <div className="form-group" style={{gridColumn: 'span 2'}}>
                <label>Beskrivelse</label>
                <input className="form-control" value={editDescription}
                  onChange={e => setEditDescription(e.target.value)}
                  placeholder="Beskrivelse" required />
              </div>
            </div>
          </div>

          <div className="card" style={{marginTop: '1rem'}}>
            <h3 style={{marginBottom: '0.75rem'}}>Posteringslinjer</h3>

            <div className="voucher-line voucher-line-with-counter" style={{fontWeight: 600, fontSize: '0.8rem', color: 'var(--gray-500)'}}>
              <span>Konto</span>
              <span>Debet</span>
              <span>Kredit</span>
              <span>Modkonto</span>
              <span>Beskrivelse</span>
              <span></span>
            </div>

            {editLines.map((line, i) => (
              <div key={i} className="voucher-line voucher-line-with-counter">
                <AccountInput accounts={accounts} value={line.account_id}
                  onChange={v => updateLine(i, 'account_id', v)}
                  placeholder="Skriv kontonr..." />
                <input type="number" className="form-control" placeholder="0,00" step="0.01" min="0"
                  value={line.debit} onChange={e => updateLine(i, 'debit', e.target.value)} />
                <input type="number" className="form-control" placeholder="0,00" step="0.01" min="0"
                  value={line.credit} onChange={e => updateLine(i, 'credit', e.target.value)} />
                <AccountInput accounts={accounts} value={line.counter_account_id}
                  onChange={v => updateLine(i, 'counter_account_id', v)}
                  placeholder="Modkonto..." />
                <input className="form-control" placeholder="Valgfri beskrivelse"
                  value={line.description} onChange={e => updateLine(i, 'description', e.target.value)} />
                <button type="button" className="btn btn-danger btn-sm" onClick={() => removeLine(i)}
                  disabled={editLines.length <= 2}>&#x2715;</button>
              </div>
            ))}

            {!isBalanced && hasEditAmounts && (
              <div className="voucher-line voucher-line-with-counter" style={{background: '#fffbeb', borderRadius: '4px', padding: '0.25rem'}}>
                <AccountInput accounts={accounts} value=""
                  onChange={v => addBalancingEntry(v)}
                  placeholder="Udlign: v\u00e6lg konto..." />
                <span style={{padding: '0.5rem', fontSize: '0.85rem', color: 'var(--gray-500)'}}>
                  {editDiff < 0 ? fmt(Math.abs(editDiff)) : ''}
                </span>
                <span style={{padding: '0.5rem', fontSize: '0.85rem', color: 'var(--gray-500)'}}>
                  {editDiff > 0 ? fmt(editDiff) : ''}
                </span>
                <span></span>
                <span style={{fontSize: '0.8rem', color: 'var(--gray-500)', padding: '0.5rem'}}>Udligning</span>
                <span></span>
              </div>
            )}

            <div className="voucher-totals voucher-totals-with-counter">
              <span>Total</span>
              <span className={isBalanced ? 'balance-ok' : 'balance-error'}>{fmt(editTotalDebit)}</span>
              <span className={isBalanced ? 'balance-ok' : 'balance-error'}>{fmt(editTotalCredit)}</span>
              <span></span>
              <span className={isBalanced ? 'balance-ok' : 'balance-error'}>
                {isBalanced ? 'I balance' : `Difference: ${fmt(Math.abs(editDiff))}`}
              </span>
              <span></span>
            </div>

            <button type="button" className="btn btn-secondary" onClick={addLine} style={{marginTop: '0.5rem'}}>
              + Tilf\u00f8j linje
            </button>
          </div>

          <div style={{display: 'flex', gap: '0.5rem', marginTop: '1rem'}}>
            <button className="btn btn-primary" disabled={saving || !isBalanced} onClick={handleSave}>
              {saving ? 'Gemmer...' : 'Gem \u00e6ndringer'}
            </button>
            <button className="btn btn-secondary" onClick={cancelEditing} disabled={saving}>Annuller</button>
          </div>
        </>
      ) : (
        <>
          <div className="card">
            <div className="form-row">
              <div>
                <strong>Dato:</strong> {new Date(voucher.date).toLocaleDateString('da-DK')}
              </div>
              <div>
                <strong>Beskrivelse:</strong> {voucher.description}
              </div>
              <div>
                <strong>Oprettet af:</strong> {voucher.created_by_name}
              </div>
            </div>
          </div>

          <div className="card" style={{marginTop: '1rem'}}>
            <h3 style={{marginBottom: '0.75rem'}}>Posteringer</h3>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Konto</th>
                    <th className="text-right">Debet</th>
                    <th className="text-right">Kredit</th>
                    <th>Beskrivelse</th>
                  </tr>
                </thead>
                <tbody>
                  {voucher.lines?.map(line => (
                    <tr key={line.id}>
                      <td>{line.account_number} - {line.account_name}</td>
                      <td className="text-right amount">{line.debit > 0 ? fmt(line.debit) : ''}</td>
                      <td className="text-right amount">{line.credit > 0 ? fmt(line.credit) : ''}</td>
                      <td>{line.description}</td>
                    </tr>
                  ))}
                  <tr className="report-total">
                    <td><strong>Total</strong></td>
                    <td className="text-right amount"><strong>{fmt(totalDebit)}</strong></td>
                    <td className="text-right amount"><strong>{fmt(totalCredit)}</strong></td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <div className="card" style={{marginTop: '1rem'}}>
        <h3 style={{marginBottom: '0.75rem'}}>Vedhæftninger</h3>

        {voucher.attachments?.length > 0 && (
          <div className="attachment-list" style={{marginBottom: '1rem'}}>
            {voucher.attachments.map(att => (
              <div key={att.id} className="attachment-item">
                <a href={`${API_BASE}/uploads/${att.filename}`}
                  target="_blank" rel="noopener noreferrer" style={{textDecoration: 'none', color: 'inherit'}}>
                  {att.original_name}
                </a>
                <button onClick={() => deleteAttachment(att.id)}
                  style={{background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', padding: '0 0.25rem', fontSize: '1rem'}}
                  title="Slet vedh\u00e6ftning">&times;</button>
              </div>
            ))}
          </div>
        )}

        <div className="file-upload" onClick={() => fileRef.current?.click()}>
          <input type="file" ref={fileRef} onChange={handleUpload}
            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp" />
          <p>{uploading ? 'Uploader...' : 'Klik her for at uploade bilag (PDF, JPG, PNG)'}</p>
        </div>
      </div>
    </div>
  );
}
